import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAttendanceStore } from '../stores/attendanceStore';
import { getTableStatus, getTableVisualization, getTotalAvailableSeats, assignRandomTable, assignBalancedTable, assignRoundRobinTable, getTableAssignmentProbabilities, syncTableOccupancy, assignTableWithNewRule, getCurrentAttendanceCount } from '../utils/tableUtils';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

const TableAssignmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedStudent } = useAttendanceStore();
  
  const [isSpinning, setIsSpinning] = useState(false);
  const [assignedTable, setAssignedTable] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [tables, setTables] = useState(getTableStatus());
  const [isScratching, setIsScratching] = useState(false);
  const [scratchProgress, setScratchProgress] = useState(0);
  const [scratchPoints, setScratchPoints] = useState<Array<{x: number, y: number, pressure: number}>>([]);
  const [revealedNumber, setRevealedNumber] = useState<number | null>(null);
  const [assignmentMethod, setAssignmentMethod] = useState<'random' | 'balanced' | 'roundRobin'>('random');
  const scratchRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    if (!selectedStudent) {
      navigate('/attendance');
    }
  }, [selectedStudent, navigate]);

  const handleStartAssignment = () => {
    if (!selectedStudent) return;

    setIsSpinning(true);
    setShowResult(false);
    setIsScratching(true);
    setScratchProgress(0);
    setScratchPoints([]);
    setRevealedNumber(null);

    // 3초 후 결과 표시
    setTimeout(() => {
      let tableNumber: number;
      
      // 새로운 배정 규칙 적용 (1-14번째는 1-7번 테이블에 골고루 배정)
      tableNumber = assignTableWithNewRule(assignmentMethod);
      
      // 학생에게 테이블 배정 정보 저장 및 출석 기록 업데이트
      const { setSelectedStudent, recordAttendance } = useAttendanceStore.getState();
      if (selectedStudent) {
        // 테이블 배정 정보가 포함된 학생 정보로 업데이트
        const updatedStudent = {
          ...selectedStudent,
          assignedTable: tableNumber
        };
        
        setSelectedStudent(updatedStudent);
        
        // 테이블 배정 정보가 포함된 출석 기록 생성
        const record = {
          studentId: selectedStudent.id,
          studentName: selectedStudent.name,
          parkingNumber: selectedStudent.parkingNumber || '',
          hasParkingSpace: selectedStudent.hasParkingSpace || false,
          date: new Date().toISOString().split('T')[0],
          assignedTable: tableNumber,
          isParkingRegistered: false
        };
        
        // 출석 기록에 추가
        const { attendanceRecords } = useAttendanceStore.getState();
        const newAttendanceRecords = [
          ...attendanceRecords.filter(r => 
            !(r.studentId === selectedStudent.id && r.date === record.date)
          ),
          record
        ];
        
        console.log('=== 테이블 배정 완료 ===');
        console.log('배정된 테이블:', tableNumber);
        console.log('업데이트된 학생:', updatedStudent);
        console.log('생성된 출석 기록:', record);
        console.log('전체 출석 기록:', newAttendanceRecords);
        
        // 테이블 점유율 동기화
        syncTableOccupancy(newAttendanceRecords);
        
        // 출석 기록 업데이트
        useAttendanceStore.setState({ attendanceRecords: newAttendanceRecords });
        
        console.log('=== 테이블 배정 처리 완료 ===');
      }
      
      setAssignedTable(tableNumber);
      setIsSpinning(false);
    }, 3000);
  };

  const handleReassign = () => {
    // 다시 배정 기능 제거
    return;
  };

  const handleConfirm = () => {
    navigate('/attendance');
  };

  const handleCloseResult = () => {
    setShowResult(false);
    setAssignedTable(null);
  };

  const handleGoToTableAssignment = () => {
    setShowResult(false);
    setAssignedTable(null);
    setIsScratching(false);
    setScratchProgress(0);
    setScratchPoints([]);
    setRevealedNumber(null);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleResetTables = () => {
    const { resetAllTableSettings } = require('../utils/tableUtils');
    resetAllTableSettings();
    
    // 출석 기록 기반으로 테이블 점유율 동기화
    const { getAttendanceByDate } = useAttendanceStore.getState();
    const todayAttendance = getAttendanceByDate(new Date().toISOString().split('T')[0]);
    syncTableOccupancy(todayAttendance);
    
    setTables(getTableStatus());
    setAssignedTable(null);
    setShowResult(false);
    setIsScratching(false);
    setScratchProgress(0);
    setScratchPoints([]);
    setRevealedNumber(null);
  };

  // 페이지 로드 시 바로 복권 긁기 시작
  useEffect(() => {
    if (selectedStudent && !isScratching && !showResult) {
      handleStartAssignment();
    }
  }, [selectedStudent]);

  // 페이지 로드 시 테이블 설정 로드 및 출석 기록 동기화
  useEffect(() => {
    const { loadTableSettings } = require('../utils/tableUtils');
    loadTableSettings();
    
    // 출석 기록 기반으로 테이블 점유율 동기화
    const { getAttendanceByDate } = useAttendanceStore.getState();
    const todayAttendance = getAttendanceByDate(new Date().toISOString().split('T')[0]);
    syncTableOccupancy(todayAttendance);
    
    setTables(getTableStatus());
  }, []);

  // 테이블 현황 실시간 업데이트
  useEffect(() => {
    const interval = setInterval(() => {
      setTables(getTableStatus());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // 스크래치 이벤트 핸들러
  const handleScratch = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isScratching || !scratchRef.current || !canvasRef.current) return;

    const rect = scratchRef.current.getBoundingClientRect();
    const canvas = canvasRef.current;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    // Canvas 좌표로 변환 (실제 Canvas 크기 기준)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    // 압력 값 계산 (터치의 경우 pressure 사용, 마우스는 기본값 0.5)
    const pressure = 'touches' in e && (e.touches[0] as any).force ? (e.touches[0] as any).force : 0.5;

    setScratchPoints(prev => [...prev, { x, y, pressure }]);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isScratching) {
      isDrawingRef.current = true;
      handleScratch(e);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isScratching && isDrawingRef.current) {
      handleScratch(e);
    }
  };

  const handleMouseUp = () => {
    isDrawingRef.current = false;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isScratching) {
      isDrawingRef.current = true;
      handleScratch(e);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isScratching && isDrawingRef.current) {
      e.preventDefault();
      handleScratch(e);
    }
  };

  const handleTouchEnd = () => {
    isDrawingRef.current = false;
  };

  // Canvas에 스크래치 효과 그리기
  const drawScratch = (x: number, y: number, pressure: number) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 압력에 따른 브러시 크기 조정 (20-40px, 더 큰 범위)
    const brushSize = 20 + (pressure * 20);
    
    // 메인 스크래치 영역 (그라데이션)
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, brushSize);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.9)');
    gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, brushSize, 0, 2 * Math.PI);
    ctx.fill();

    // 스크래치 텍스처 효과 (불규칙한 모양)
    for (let i = 0; i < 5; i++) {
      const offsetX = (Math.random() - 0.5) * brushSize * 0.8;
      const offsetY = (Math.random() - 0.5) * brushSize * 0.8;
      const dotSize = Math.random() * 4 + 2;
      const opacity = 0.2 + Math.random() * 0.6;
      
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.beginPath();
      ctx.arc(x + offsetX, y + offsetY, dotSize, 0, 2 * Math.PI);
      ctx.fill();
    }

    // 가장자리 블러 효과 (작은 점들로 자연스러운 경계)
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const distance = brushSize * (0.7 + Math.random() * 0.3);
      const offsetX = Math.cos(angle) * distance;
      const offsetY = Math.sin(angle) * distance;
      const dotSize = Math.random() * 2 + 1;
      
      ctx.fillStyle = `rgba(255, 255, 255, ${0.1 + Math.random() * 0.3})`;
      ctx.beginPath();
      ctx.arc(x + offsetX, y + offsetY, dotSize, 0, 2 * Math.PI);
      ctx.fill();
    }
  };

  // Canvas 크기 설정
  useEffect(() => {
    if (canvasRef.current && scratchRef.current) {
      const canvas = canvasRef.current;
      const rect = scratchRef.current.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
  }, [isScratching]);

  // 스크래치 포인트가 변경될 때마다 Canvas 업데이트 및 진행률 계산
  useEffect(() => {
    if (canvasRef.current && scratchPoints.length > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Canvas 초기화
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // 모든 스크래치 포인트 다시 그리기
      scratchPoints.forEach(point => {
        drawScratch(point.x, point.y, point.pressure);
      });

      // 스크래치 진행률 계산 (더 천천히 증가 - 드래그 시간 연장)
      const progress = Math.min(scratchPoints.length * 0.025, 1); // 약 40번 긁어야 100%
      setScratchProgress(progress);

      // 30% 이상 긁으면 번호 드러나기 시작
      if (progress >= 0.3 && !revealedNumber && assignedTable) {
        setRevealedNumber(assignedTable);
      }

      // 70% 이상 긁으면 완료
      if (progress >= 0.7 && !showResult) {
        setShowResult(true);
        setIsScratching(false);
      }
    }
  }, [scratchPoints, revealedNumber, assignedTable, showResult]);

  if (!selectedStudent) {
    return null;
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 헤더 */}
          <Card className="mb-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">🎲 테이블 배정</h1>
              <p className="text-gray-600">원리교육 프로그램</p>
            </div>
          </Card>

          {/* 배정 방식 선택 */}
          {!isScratching && !showResult && (
            <Card className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">📋 배정 방식 선택</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setAssignmentMethod('random')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    assignmentMethod === 'random'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="text-2xl mb-2">🎲</div>
                  <div className="font-semibold mb-1">가중치 랜덤</div>
                  <div className="text-sm text-gray-600">빈 자리가 많을수록 높은 확률</div>
                </button>
                
                <button
                  onClick={() => setAssignmentMethod('balanced')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    assignmentMethod === 'balanced'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                  }`}
                >
                  <div className="text-2xl mb-2">⚖️</div>
                  <div className="font-semibold mb-1">균형 배정</div>
                  <div className="text-sm text-gray-600">가장 빈 자리가 많은 테이블</div>
                </button>
                
                <button
                  onClick={() => setAssignmentMethod('roundRobin')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    assignmentMethod === 'roundRobin'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className="text-2xl mb-2">🔄</div>
                  <div className="font-semibold mb-1">순환 배정</div>
                  <div className="text-sm text-gray-600">가장 적은 사람이 있는 테이블</div>
                </button>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-700">
                  <strong>🎯 새로운 배정 규칙:</strong>
                  <br />
                  • 1-7번째: 1-7번 테이블에 랜덤 배정 (한 명씩)
                  <br />
                  • 8-14번째: 1-7번 테이블에 랜덤 배정 (두 명씩)
                  <br />
                  • 15번째부터: {assignmentMethod === 'random' && '가중치 랜덤'}
                  {assignmentMethod === 'balanced' && '균형 배정'}
                  {assignmentMethod === 'roundRobin' && '순환 배정'} 규칙 적용
                </div>
              </div>
            </Card>
          )}

          {/* 복권 긁기 (중앙 배치) */}
          {!showResult && (
            <Card className="mb-6 text-center">
              <motion.div
                animate={isSpinning ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.5, repeat: isSpinning ? Infinity : 0 }}
                className="flex flex-col items-center justify-center min-h-[500px] relative"
              >
                {/* 배경 효과 */}
                <motion.div
                  animate={isSpinning ? { 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3]
                  } : {}}
                  transition={{ duration: 2, repeat: isSpinning ? Infinity : 0 }}
                  className="absolute inset-0 bg-gradient-to-br from-[#05FB73]/20 to-[#FE73F3]/20 rounded-3xl blur-xl"
                />

                {/* 복권 긁기 영역 */}
                {isScratching && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full max-w-md mx-auto relative z-10"
                  >
                    <div className="text-2xl font-bold text-gray-800 mb-6">🎫 테이블 배정 복권</div>
                    <div className="text-sm text-gray-600 mb-2">
                      {assignmentMethod === 'random' && '🎲 가중치 랜덤 배정'}
                      {assignmentMethod === 'balanced' && '⚖️ 균형 배정'}
                      {assignmentMethod === 'roundRobin' && '🔄 순환 배정'}
                    </div>
                    <div className="text-sm text-blue-600 mb-4">
                      현재 출석: {getCurrentAttendanceCount()}명 
                      {getCurrentAttendanceCount() < 7 && ' (1-7번 테이블 랜덤 배정)'}
                      {getCurrentAttendanceCount() >= 7 && getCurrentAttendanceCount() < 14 && ' (1-7번 테이블 추가 배정)'}
                      {getCurrentAttendanceCount() >= 14 && ' (기존 규칙 적용)'}
                    </div>
                    
                    {/* 트렌디한 복권 카드 */}
                    <div
                      ref={scratchRef}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      className="relative w-full aspect-square bg-gradient-to-br from-[#05FB73] via-[#00B3FF] to-[#FE73F3] rounded-3xl overflow-hidden cursor-crosshair select-none shadow-2xl border-4 border-white/30"
                      style={{ 
                        touchAction: 'none',
                        background: 'linear-gradient(135deg, #05FB73 0%, #00B3FF 50%, #FE73F3 100%)'
                      }}
                    >
                      {/* 스크래치 오버레이 - 트렌디한 디자인 */}
                      <div className="absolute inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-8xl mb-6">🎯</div>
                          <div className="text-white/90 text-lg font-medium mb-2">여기를 긁어보세요</div>
                          <div className="text-white/60 text-sm">
                            진행률: {Math.round(scratchProgress * 100)}%
                          </div>
                        </div>
                      </div>
                      
                      {/* 스크래치 Canvas - 자연스러운 스크래치 효과 */}
                      <canvas
                        ref={canvasRef}
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        style={{ 
                          mixBlendMode: 'destination-out' as any,
                          filter: 'blur(1px)'
                        }}
                        width={400}
                        height={400}
                      />
                      
                      {/* 진행률 표시 - 트렌디한 스타일 */}
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full h-2 border border-white/30">
                          <div
                            className="bg-gradient-to-r from-[#05FB73] to-[#FE73F3] h-2 rounded-full transition-all duration-300 shadow-lg"
                            style={{ width: `${scratchProgress * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 드러난 번호 - 트렌디한 카드 */}
                    {revealedNumber && (
                      <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className="mt-8 p-8 bg-white/95 backdrop-blur-md rounded-3xl border-2 border-[#05FB73]/20 shadow-2xl"
                      >
                        <div className="text-center">
                          <div className="text-3xl text-gray-700 mb-4 font-bold">🎉 당첨!</div>
                          <div className="text-7xl font-black bg-gradient-to-r from-[#05FB73] via-[#00B3FF] to-[#FE73F3] bg-clip-text text-transparent mb-2">
                            {revealedNumber}
                          </div>
                          <div className="text-xl text-gray-600 font-medium">번 테이블</div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
                
                {isSpinning && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-8"
                  >
                    <div className="text-gray-600 text-xl font-medium">
                      복권을 준비하는 중입니다... 🎰
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </Card>
          )}

          {/* 결과 표시 */}
          <AnimatePresence>
            {showResult && assignedTable && (
              <>
                {/* 블러 배경 */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                  onClick={() => {}} // 배경 클릭 방지
                />
                
                {/* 결과 카드 */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.5 }}
                  className="fixed inset-0 flex items-center justify-center z-50 p-4"
                >
                  <div className="w-full max-w-2xl mx-auto text-center shadow-2xl relative bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 rounded-3xl backdrop-blur-md p-8">
                    {/* 닫기 버튼 */}
                    <button
                      onClick={handleCloseResult}
                      className="absolute top-6 right-6 w-10 h-10 bg-white/60 hover:bg-white/80 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all duration-200 shadow-md"
                    >
                      ✕
                    </button>
                    
                    <div className="text-8xl mb-2">🎉</div>
                    <h2 className="text-2xl font-bold text-gray-700 mb-3">결과 발표</h2>
                    
                    <div className="py-3 mb-2">
                      <p className="text-2xl text-gray-700 mb-2 font-medium">
                        <span className="font-bold text-gray-800">{selectedStudent.name}</span>님의
                      </p>
                      <p className="text-xl text-gray-600 mb-3">오늘의 테이블 번호는?</p>
                      <p className="text-[18rem] font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-1 leading-none">
                        {revealedNumber || assignedTable}
                      </p>
                      <p className="text-2xl text-gray-700 font-medium">테이블입니다!</p>
                    </div>
                    
                    <div className="text-gray-600 text-xl font-medium mb-3">
                      잔여석: {getTotalAvailableSeats()}석
                    </div>

                    {/* 액션 버튼 */}
                    <div className="flex justify-center">
                      <button
                        onClick={handleConfirm}
                        className="px-12 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        ✅ 확인
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* 테이블 현황 */}
          <Card className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">테이블 현황</h2>
              <button
                onClick={handleResetTables}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-all duration-300 hover:scale-105"
              >
                🔄 초기화
              </button>
            </div>
            <div className="space-y-3">
              {tables.map((table) => {
                const probabilities = getTableAssignmentProbabilities(assignmentMethod);
                const tableProbability = probabilities.find(p => p.tableNumber === table.tableNumber);
                
                return (
                  <motion.div
                    key={table.tableNumber}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: table.tableNumber * 0.1 }}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      assignedTable === table.tableNumber
                        ? 'bg-accent/20 border-2 border-accent'
                        : 'bg-white/10'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">
                        {assignedTable === table.tableNumber ? '🎯' : '📋'}
                      </span>
                      <span className="text-gray-800 font-medium">
                        {table.tableNumber}번({table.type})
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="text-gray-600 font-mono text-sm">
                        {getTableVisualization(table)}
                      </div>
                      <span className="text-gray-500 text-sm">
                        잔여: {table.capacity - table.currentOccupancy}석
                      </span>
                      {tableProbability && table.capacity > table.currentOccupancy && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          tableProbability.probability > 50 
                            ? 'bg-green-100 text-green-700' 
                            : tableProbability.probability > 20 
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {tableProbability.probability.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
            
            {/* 확률 설명 */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">
                <strong>📊 배정 확률:</strong> 
                {assignmentMethod === 'random' && ' 빈 자리가 많을수록 높은 확률로 배정됩니다.'}
                {assignmentMethod === 'balanced' && ' 가장 빈 자리가 많은 테이블에 배정됩니다.'}
                {assignmentMethod === 'roundRobin' && ' 가장 적은 사람이 있는 테이블에 배정됩니다.'}
              </div>
            </div>
          </Card>

          {/* 액션 버튼들 */}
          <div className="grid grid-cols-1 gap-4">
            {showResult && (
              <button
                onClick={handleConfirm}
                className="w-full px-8 py-6 bg-green-600 hover:bg-green-700 text-white font-bold text-xl rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
              >
                ✅ 확인
              </button>
            )}
            
            <button
              onClick={handleGoHome}
              className="w-full px-8 py-6 bg-gray-800 hover:bg-gray-900 text-white font-bold text-xl rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              🏠 홈으로
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TableAssignmentPage; 